import { LoadingButton } from '@mui/lab'
import { Box, Button, Stack, Typography, useTheme } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { adminCreateOrg } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { AdminOrganizationNew, AuthProvider, OrganizationFull, Product } from 'api/model'
import JelledModal, { ModalButtonsContainerStack } from 'components/JelledModal'
import CityAutocomplete from 'components/Organization/CityAutocomplete'
import IndustryPicker from 'components/Organization/IndustryPicker'
import { useFetchProducts } from 'hooks/use-admin'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useEffect, useState } from 'react'
import { useSetRecoilState } from 'recoil'
import { toastAtom } from 'state'
import { isFieldEmpty, isWebAddressValid } from 'utils'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import Selector from 'components/Selector'
import { COLORS } from 'app-constants'

const AdminAddOrganization = ({ onFinished }: { onFinished: (newOrg?: OrganizationFull) => void }) => {
  const theme = useTheme()

  const isMobile = useCheckMobileScreen()

  const setToast = useSetRecoilState(toastAtom)
  const { products } = useFetchProducts()

  const [product, setProduct] = useState<Product | null>(null)
  useEffect(() => {
    if (!product) {
      setProduct(products[0])
    }
  }, [products, setProduct, product])

  const authProviders: AuthProvider[] = Object.values(AuthProvider)

  const [name, setName] = useState('')
  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }

  const [domain, setDomain] = useState('')
  const onDomainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDomain(event.target.value)
  }

  const [authProvider, setAuthProvider] = useState<AuthProvider | null>(authProviders[0])

  const [description, setDescription] = useState('')
  const onDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value)
  }

  const [website, setWebsite] = useState('')
  const onWebsiteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWebsite(event.target.value)
  }

  const [location, setLocation] = useState('')
  const onLocationChange = (value: string | null) => {
    setLocation(value ?? '')
  }

  const [industry, setIndustry] = useState('')

  const [year, setYear] = useState<Date | null>(null)
  const onYearChange = (date: Date | null | undefined) => {
    if (!date) {
      setYear(null)
      return
    }

    setYear(date)
  }

  const [isSubmitting, setIsSubmitting] = useState(false)
  const onFormSubmit = async () => {
    if (isFieldEmpty(name) || isFieldEmpty(domain)) {
      return
    }

    try {
      setIsSubmitting(true)

      const newOrg: AdminOrganizationNew = {
        name,
        email_domain: domain,
        product_id: !!product ? product.id : undefined,
        auth_provider: !!authProvider ? (authProvider as AuthProvider) : undefined,
        description,
        website,
        location,
        industry,
        founding_year: year?.getFullYear(),
      }
      const results = await adminCreateOrg(newOrg)
      onFinished(results.data)
      setToast({ title: 'Account added successfully', severity: 'success' })
    } catch (error) {
      setToast({ title: 'Failed to add account', severity: 'error' })
      onFinished()
    } finally {
      setIsSubmitting(false)
    }
  }

  const onProductChange = (option: Product) => {
    setProduct(option)
  }

  return (
    <JelledModal
      onClose={onFinished}
      modalStyle={{
        background: COLORS.PURE_WHITE,
        marginLeft: isMobile ? '16px' : 0,
        marginRight: isMobile ? '16px' : 0,
        width: isMobile ? '100%' : '960px',
      }}>
      <Typography variant="h4" mb={4} color="secondary">
        Create new account
      </Typography>

      <Box component="form">
        <Stack rowGap={4}>
          <TextFieldWithDarkMode
            className="has-label"
            isDarkBackdrop={false}
            theme={theme}
            label="Company name"
            placeholder="Enter name"
            value={name}
            onChange={onNameChange}
            disabled={isSubmitting}
            error={isFieldEmpty(name)}
          />

          <TextFieldWithDarkMode
            className="has-label"
            isDarkBackdrop={false}
            theme={theme}
            label="Email domain"
            placeholder="Enter email domain"
            value={domain}
            onChange={onDomainChange}
            disabled={isSubmitting}
            error={isFieldEmpty(domain)}
          />

          <Selector
            id="product-selector"
            label="Product"
            option={product}
            setOption={onProductChange}
            options={products}
            isDarkBackdrop={false}
            width="240px"
          />

          <Selector
            label="Auth provider"
            id="auth-provider-selector"
            option={authProvider}
            setOption={setAuthProvider}
            options={authProviders}
            isDarkBackdrop={false}
            width="240px"
          />

          <TextFieldWithDarkMode
            className="has-label"
            theme={theme}
            isDarkBackdrop={false}
            multiline
            rows={3}
            label="Description"
            placeholder="Add here"
            value={description}
            onChange={onDescriptionChange}
            disabled={isSubmitting}
          />

          <TextFieldWithDarkMode
            className="has-label"
            theme={theme}
            isDarkBackdrop={false}
            label="Company website"
            placeholder="mycompany.com"
            value={website}
            onChange={onWebsiteChange}
            disabled={isSubmitting}
            error={!isWebAddressValid(website, true)}
            helperText={!isWebAddressValid(website, true) && 'You must enter a valid website'}
          />

          <CityAutocomplete initialValue={location} onValueChanged={onLocationChange} disabled={isSubmitting} />

          <IndustryPicker value={industry} setIndustry={setIndustry} disabled={isSubmitting} />

          <DatePicker
            views={['year']}
            className="has-label"
            label="Founding year"
            value={year}
            onChange={onYearChange}
            disableFuture
            disabled={isSubmitting}
            renderInput={(params) => {
              const inputProps = Object.assign({}, params.inputProps, {
                placeholder: 'Add year',
              })
              const customParams = Object.assign({}, params, { inputProps })
              return <TextFieldWithDarkMode isDarkBackdrop={false} theme={theme} {...customParams} />
            }}
          />
        </Stack>
        <ModalButtonsContainerStack>
          <Button size="medium" color="secondary" onClick={() => onFinished()} disabled={isSubmitting}>
            Cancel
          </Button>
          <LoadingButton
            loading={isSubmitting}
            variant="contained"
            size="medium"
            color="primary"
            onClick={onFormSubmit}
            disabled={!name || !domain}>
            Save
          </LoadingButton>
        </ModalButtonsContainerStack>
      </Box>
    </JelledModal>
  )
}

export default AdminAddOrganization
